import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Edit,
  Delete,
  Warning,
  Add,
  MoveUp,
  CloudUpload,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import moment from 'moment-timezone';
import useApiError from '../hooks/useApiError';
import { useTimezone } from '../contexts/TimezoneContext';
import { getAllRooms, deleteRoom, updateRoom, uploadRoomImage, deleteRoomImage } from '../services/roomService';
import { getRoomFields } from '../services/roomFieldService';
import { getBookingsByRoom, updateBooking } from '../services/bookingService';
import { isFutureBookingInOrgTimezone, formatInOrgTimezone } from '../utils/timezoneUtils';
import RoomCard from '../components/RoomCard';
import ManageRoomsPageSkeleton from '../components/ManageRoomsPageSkeleton';

export default function ManageRoomsPage() {
  const [rooms, setRooms] = useState([]);
  const [roomFields, setRoomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Edit room dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    capacity: '',
    location: '',
    notes: '',
    customFields: {}
  });
  
  // Image editing state
  const [selectedNewImage, setSelectedNewImage] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [currentRoomImage, setCurrentRoomImage] = useState(null);
  
  // Delete room dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingRoom, setDeletingRoom] = useState(null);
  const [futureBookings, setFutureBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingMigrations, setBookingMigrations] = useState({});
  
  const { enqueueSnackbar } = useSnackbar();
  const { orgTimeZone } = useTimezone();
  const handleApiError = useApiError();

  const loadRoomsData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const [roomsData, fieldsData] = await Promise.all([
        getAllRooms(),
        getRoomFields()
      ]);
      
      setRooms(roomsData);
      setRoomFields(fieldsData || []);
    } catch (err) {
      setError('Failed to load rooms data');
      handleApiError(err, 'Failed to load rooms data');
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  useEffect(() => {
    loadRoomsData();
  }, [loadRoomsData]);

  const renderFieldValue = (field, value) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    switch (field.type) {
      case 'boolean':
        return value === true || value === 'true' ? 'Yes' : 'No';
      case 'select':
        return value;
      case 'number':
        return value.toString();
      default:
        return value;
    }
  };

  const renderRoomFields = (room) => {
    if (!roomFields.length) {
      return (
        <Box>
          {room.capacity && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Capacity:</strong> {room.capacity}
            </Typography>
          )}
          {room.location && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Location:</strong> {room.location}
            </Typography>
          )}
          {room.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Notes:</strong> {room.notes}
            </Typography>
          )}
        </Box>
      );
    }

    return roomFields
      .sort((a, b) => a.order - b.order)
      .map(field => {
        let value;
        
        if (field.isSystem) {
          value = room[field.name];
        } else {
          const customFields = room.customFields instanceof Map 
            ? Object.fromEntries(room.customFields)
            : room.customFields;
          value = customFields?.[field.name];
        }

        const displayValue = renderFieldValue(field, value);
        
        if (!displayValue) return null;

        return (
          <Typography key={field._id} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            <strong>{field.label}:</strong> {displayValue}
          </Typography>
        );
      })
      .filter(Boolean);
  };

  const handleEditRoom = (room) => {
    setEditingRoom(room);
    
    // Initialize form data
    const formData = {
      name: room.name || '',
      capacity: room.capacity || '',
      location: room.location || '',
      notes: room.notes || '',
      customFields: {}
    };

    // Initialize custom fields
    const customFields = room.customFields instanceof Map 
      ? Object.fromEntries(room.customFields)
      : room.customFields || {};
    
    roomFields.forEach(field => {
      if (!field.isSystem) {
        formData.customFields[field.name] = customFields[field.name] || '';
      }
    });

    setEditFormData(formData);
    
    // Set current room image
    const roomImage = room.images && room.images.length > 0 ? room.images[0] : null;
    setCurrentRoomImage(roomImage);
    setSelectedNewImage(null);
    
    setEditDialogOpen(true);
  };

  const handleImageSelect = useCallback((files) => {
    if (!files || files.length === 0) return;
    
    const file = files[0]; // Take only the first file
    
    // Basic validation
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar('Invalid file type. Please upload a JPEG, PNG, or WebP image.', { variant: 'error' });
      return;
    }
    
    if (file.size > maxSize) {
      enqueueSnackbar('File size exceeds 5MB limit.', { variant: 'error' });
      return;
    }
    
    setSelectedNewImage(file);
  }, [enqueueSnackbar]);

  const handleRemoveImage = useCallback(async () => {
    if (!editingRoom || !currentRoomImage) return;
    
    try {
      setIsUploadingImage(true);
      await deleteRoomImage(editingRoom._id, currentRoomImage._id);
      
      // Update local state
      setCurrentRoomImage(null);
      setRooms(rooms.map(room => 
        room._id === editingRoom._id 
          ? { ...room, images: [] }
          : room
      ));
      
      enqueueSnackbar('Image deleted successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error deleting image:', err);
      enqueueSnackbar('Failed to delete image', { variant: 'error' });
    } finally {
      setIsUploadingImage(false);
    }
  }, [editingRoom, currentRoomImage, rooms, enqueueSnackbar]);

  const handleSaveEdit = async () => {
    if (!editingRoom) return;

    try {
      setIsUploadingImage(true);
      
      // Update room data first
      const updateData = {
        name: editFormData.name,
        capacity: editFormData.capacity ? parseInt(editFormData.capacity) : undefined,
        location: editFormData.location,
        notes: editFormData.notes,
        customFields: editFormData.customFields
      };

      await updateRoom(editingRoom._id, updateData);
      
      // Upload new image if selected
      let newImageData = null;
      if (selectedNewImage) {
        try {
          const uploadResponse = await uploadRoomImage(editingRoom._id, selectedNewImage, 'gallery');
          newImageData = uploadResponse.image;
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          enqueueSnackbar('Room updated but failed to upload image', { variant: 'warning' });
        }
      }
      
      // Update local state
      const updatedRoom = { 
        ...editingRoom, 
        ...updateData,
        images: newImageData ? [newImageData] : (currentRoomImage ? [currentRoomImage] : [])
      };
      
      setRooms(rooms.map(room => 
        room._id === editingRoom._id ? updatedRoom : room
      ));

      enqueueSnackbar('Room updated successfully', { variant: 'success' });
      setEditDialogOpen(false);
      setEditingRoom(null);
      setSelectedNewImage(null);
    } catch (err) {
      console.error('Error updating room:', err);
      enqueueSnackbar('Failed to update room', { variant: 'error' });
    } finally {
      setIsUploadingImage(false);
    }
  };

  // Memoize image preview URL to prevent recreation on every render
  const imagePreviewUrl = useMemo(() => {
    return selectedNewImage ? URL.createObjectURL(selectedNewImage) : null;
  }, [selectedNewImage]);

  // Cleanup URL when component unmounts or image changes
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const handleDeleteRoom = async (room) => {
    setDeletingRoom(room);
    setLoadingBookings(true);
    
    try {
      // Load future bookings for this room
      const bookingsData = await getBookingsByRoom(room._id);
      const futureBookingsData = bookingsData.filter(booking => 
        isFutureBookingInOrgTimezone(booking.endTime, orgTimeZone)
      );
      
      setFutureBookings(futureBookingsData);
      
      // Initialize booking migrations
      const migrations = {};
      futureBookingsData.forEach(booking => {
        migrations[booking._id] = '';
      });
      setBookingMigrations(migrations);
      
    } catch (err) {
      console.error('Error loading bookings:', err);
      enqueueSnackbar('Failed to load room bookings', { variant: 'error' });
    } finally {
      setLoadingBookings(false);
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingRoom) return;

    try {
      // First, migrate bookings if any
      const bookingsToMigrate = Object.entries(bookingMigrations)
        .filter(([bookingId, targetRoomId]) => targetRoomId && targetRoomId !== '' && targetRoomId !== 'DELETE');

      // Track successful migrations and failures
      let successfulMigrations = 0;
      const migrationErrors = [];

      for (const [bookingId, targetRoomId] of bookingsToMigrate) {
        const booking = futureBookings.find(b => b._id === bookingId);
        if (booking) {
          try {
            await updateBooking(bookingId, {
              roomId: targetRoomId,
              startTime: booking.startTime,
              endTime: booking.endTime
            });
            successfulMigrations++;
          } catch (migrationError) {
            console.error('Migration error for booking:', bookingId, migrationError);
            const targetRoom = rooms.find(r => r._id === targetRoomId);
            const timeSlot = `${formatInOrgTimezone(booking.startTime, orgTimeZone)} - ${formatInOrgTimezone(booking.endTime, orgTimeZone)}`;
            
            if (migrationError.response?.status === 409) {
              migrationErrors.push(`Cannot migrate booking "${timeSlot}" to "${targetRoom?.name}" - time slot is already occupied`);
            } else {
              migrationErrors.push(`Failed to migrate booking "${timeSlot}" to "${targetRoom?.name}": ${migrationError.response?.data?.error || migrationError.message}`);
            }
          }
        }
      }

      // Show migration errors if any
      if (migrationErrors.length > 0) {
        const errorMessage = `Migration failed:\n${migrationErrors.join('\n')}\n\nPlease choose different rooms or delete the conflicting bookings.`;
        enqueueSnackbar(errorMessage, { 
          variant: 'error',
          persist: true,
          style: { whiteSpace: 'pre-line' }
        });
        return; // Don't proceed with room deletion
      }

      // Then delete the room
      await deleteRoom(deletingRoom._id);

      // Update local state
      setRooms(rooms.filter(room => room._id !== deletingRoom._id));

      const totalBookings = futureBookings.length;
      const deletedBookings = totalBookings - successfulMigrations;

      let successMessage = `Room "${deletingRoom.name}" deleted successfully`;
      if (successfulMigrations > 0) {
        successMessage += ` and ${successfulMigrations} booking(s) migrated`;
      }
      if (deletedBookings > 0) {
        successMessage += ` and ${deletedBookings} booking(s) deleted`;
      }

      enqueueSnackbar(successMessage, { variant: 'success' });
      
      setDeleteDialogOpen(false);
      setDeletingRoom(null);
      setFutureBookings([]);
      setBookingMigrations({});
    } catch (err) {
      console.error('Error deleting room:', err);
      enqueueSnackbar(
        `Failed to delete room: ${err.response?.data?.error || err.message}`,
        { variant: 'error' }
      );
    }
  };

  const renderEditFormField = (field) => {
    // Safety check to prevent accessing undefined properties
    if (!editFormData || !editFormData.customFields) {
      return null;
    }
    
    const value = field.isSystem 
      ? editFormData[field.name]
      : editFormData.customFields[field.name];

    const handleChange = (newValue) => {
      if (field.isSystem) {
        setEditFormData(prev => ({
          ...prev,
          [field.name]: newValue
        }));
      } else {
        setEditFormData(prev => ({
          ...prev,
          customFields: {
            ...prev.customFields,
            [field.name]: newValue
          }
        }));
      }
    };

    switch (field.type) {
      case 'boolean':
        return (
          <FormControlLabel
            key={field._id}
            control={
              <Checkbox
                checked={value === true || value === 'true'}
                onChange={(e) => handleChange(e.target.checked)}
              />
            }
            label={field.label}
          />
        );
      case 'select':
        return (
          <FormControl key={field._id} fullWidth margin="normal">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
              label={field.label}
            >
              {field.options?.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'number':
        return (
          <TextField
            key={field._id}
            fullWidth
            margin="normal"
            label={field.label}
            type="number"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
          />
        );
      default:
        return (
          <TextField
            key={field._id}
            fullWidth
            margin="normal"
            label={field.label}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
          />
        );
    }
  };

  const getAvailableRoomsForMigration = (excludeRoomId) => {
    return rooms.filter(room => room._id !== excludeRoomId);
  };

  if (loading) {
    return <ManageRoomsPageSkeleton />;
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Manage Rooms
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          href="/create-room"
        >
          Create New Room
        </Button>
      </Box>

      {rooms.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No rooms found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create your first room to get started.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {rooms.map((room) => (
            <Grid item xs={12} md={6} lg={4} key={room._id}>
              <RoomCard 
                room={room}
                adminMode={true}
                onEdit={handleEditRoom}
                onDelete={handleDeleteRoom}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Room Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Room</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Room Name"
            value={editFormData.name || ''}
            onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
            required
          />
          
          {roomFields
            .sort((a, b) => a.order - b.order)
            .map(field => renderEditFormField(field))}

          <Divider sx={{ my: 3 }} />
          
          {/* Room Image Section */}
          <Typography variant="h6" gutterBottom>
            Room Image
          </Typography>
          
          {/* Current Image Display */}
          {(currentRoomImage || selectedNewImage) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {selectedNewImage ? 'New Image Preview' : 'Current Image'}
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  width: 300,
                  height: 200,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'hidden',
                  backgroundColor: 'grey.100'
                }}
              >
                <img
                  src={selectedNewImage 
                    ? imagePreviewUrl 
                    : (currentRoomImage?.thumbnailUrl || currentRoomImage?.url)
                  }
                  alt="Room image"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                {!isUploadingImage && (
                  <Button
                    size="small"
                    onClick={selectedNewImage 
                      ? () => setSelectedNewImage(null)
                      : handleRemoveImage
                    }
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      minWidth: 'auto',
                      width: 32,
                      height: 32,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      color: 'error.main',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)'
                      }
                    }}
                  >
                    ×
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Upload Area */}
          {!selectedNewImage && (
            <Box>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageSelect(e.target.files)}
                style={{ display: 'none' }}
                id="edit-room-image-input"
                disabled={isUploadingImage}
              />
              <label htmlFor="edit-room-image-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={isUploadingImage ? <CircularProgress size={20} /> : <CloudUpload />}
                  disabled={isUploadingImage}
                  sx={{ mb: 1 }}
                >
                  {currentRoomImage ? 'Change Image' : 'Upload Image'}
                </Button>
              </label>
              <Typography variant="body2" color="text.secondary">
                Upload a new image (JPG, PNG, WebP, max 5MB)
              </Typography>
            </Box>
          )}

          {isUploadingImage && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Processing image...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={isUploadingImage}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={isUploadingImage}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Room Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Warning sx={{ mr: 1, color: 'warning.main' }} />
          Delete Room "{deletingRoom?.name}"
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>This action cannot be undone!</strong>
            </Typography>
            <Typography variant="body2">
              All room data will be permanently deleted. If there are future bookings, 
              you can migrate them to other rooms before deletion.
            </Typography>
          </Alert>

          {loadingBookings ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Loading future bookings...
              </Typography>
            </Box>
          ) : (
            <>
              {futureBookings.length > 0 ? (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Future Bookings ({futureBookings.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    These bookings will be deleted unless you migrate them to another room:
                  </Typography>
                  
                  <List>
                    {futureBookings.map((booking) => (
                      <ListItem key={booking._id} divider>
                        <ListItemText
                          primary={`${formatInOrgTimezone(booking.startTime, orgTimeZone)} - ${formatInOrgTimezone(booking.endTime, orgTimeZone)}`}
                          secondary={`Booked by: ${booking.user?.username || 'Unknown'}`}
                        />
                        <ListItemSecondaryAction>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Action</InputLabel>
                            <Select
                              value={bookingMigrations[booking._id] || 'DELETE'}
                              onChange={(e) => setBookingMigrations(prev => ({
                                ...prev,
                                [booking._id]: e.target.value
                              }))}
                              label="Action"
                            >
                              <MenuItem value="DELETE">
                                <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                                  <Delete sx={{ mr: 1, fontSize: 18 }} />
                                  <strong>Delete this booking</strong>
                                </Box>
                              </MenuItem>
                              {getAvailableRoomsForMigration(deletingRoom?._id).map(room => (
                                <MenuItem key={room._id} value={room._id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                                    <MoveUp sx={{ mr: 1, fontSize: 18 }} />
                                    <strong>Migrate to "{room.name}"</strong>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    No future bookings found for this room.
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            disabled={loadingBookings}
          >
            Delete Room
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
} 